// File: src/components/Dashboard/DashboardHeader.jsx

import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Download, Filter, Sparkles } from "lucide-react";
import DemoAISummary from "./DemoAISummary";

function DashboardHeader({ onFilter, user }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [matchType, setMatchType] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const greeting = useMemo(() => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  }, [currentTime]);

  const firstName = useMemo(() => {
    const fullName = String(user?.full_name || "").trim();
    return fullName ? fullName.split(" ")[0] : "Manager";
  }, [user]);

  const formattedTime = currentTime.toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const applyFilters = () => {
    onFilter?.({
      matchType,
      startDate,
      endDate,
    });
  };

  const clearFilters = () => {
    setMatchType("All");
    setStartDate("");
    setEndDate("");
    onFilter?.({
      matchType: "All",
      startDate: "",
      endDate: "",
    });
  };

  const handleDownloadZip = async () => {
    try {
      setDownloading(true);

      const response = await fetch("/solo-mdsh-api/staffing/reports/weekly-report-zip", {
        headers: {
          "x-user-email": user?.email || "",
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "weekly-report.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("ZIP download failed:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {user?.is_demo_user && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 shadow-sm">
          🧪 You are currently viewing the system in <strong>Demo Mode</strong>. Changes are disabled.
        </div>
      )}

      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-cyan-50 p-6 shadow-sm">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800">
              <Sparkles size={14} />
              Executive Dashboard
            </div>

            <h1 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900">
              {greeting}, {firstName} 👋
            </h1>

            <p className="mt-2 text-sm text-slate-600">
              A live view of revenue, staffing, procurement, and operational performance.
            </p>

            <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-500">
              <CalendarDays size={16} />
              <span>Last updated: {formattedTime}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleDownloadZip}
              disabled={downloading}
              className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
              type="button"
            >
              <Download size={16} />
              {downloading ? "Preparing ZIP..." : "Download Weekly ZIP"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-sky-700" />
          <h2 className="text-lg font-bold text-slate-900">Dashboard Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-slate-700 mb-2">Service Level</label>
            <select
              value={matchType}
              onChange={(e) => setMatchType(e.target.value)}
              className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            >
              <option value="All">All</option>
              <option value="Full House">Full House</option>
              <option value="Busy">Busy</option>
              <option value="50/50">50/50</option>
              <option value="Quiet">Quiet</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-slate-700 mb-2">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-slate-700 mb-2">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={applyFilters}
              className="w-full rounded-2xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
              type="button"
            >
              Apply Filters
            </button>
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              type="button"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <DemoAISummary />
      </div>
    </div>
  );
}

export default DashboardHeader;